
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Download, Clock, FileText, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { 
  listCertificateBackups, 
  restoreFromLocalCertificateBackup, 
  CertificateBackupMeta 
} from '@/services/backup/certificateBackup';
import { formatDistance } from 'date-fns';

const CertificateBackupList = () => {
  const [backups, setBackups] = useState<CertificateBackupMeta[]>([]);
  const [isRestoring, setIsRestoring] = useState<string | null>(null);
  
  useEffect(() => {
    refreshBackupList();
  }, []);
  
  const refreshBackupList = () => {
    const backupsList = listCertificateBackups();
    setBackups(backupsList);
  };
  
  const handleRestore = async (backupId: string) => {
    try {
      setIsRestoring(backupId);
      await restoreFromLocalCertificateBackup(backupId);
      refreshBackupList();
    } catch (err) {
      console.error('Error restoring backup:', err);
      toast.error('Failed to restore backup');
    } finally {
      setIsRestoring(null);
    }
  };
  
  if (backups.length === 0) {
    return (
      <Card className="p-6 flex flex-col items-center justify-center text-center">
        <FileText className="h-10 w-10 text-muted-foreground mb-2" />
        <h3 className="font-medium mb-1">No Certificate Backups Found</h3>
        <p className="text-sm text-muted-foreground">
          Create a backup to protect your certificate templates and certification data
        </p>
      </Card>
    );
  }
  
  return (
    <ScrollArea className="h-[200px] rounded-md border">
      <div className="p-2 space-y-2">
        {backups.map((backup) => (
          <Card 
            key={backup.id} 
            className="p-2 text-sm flex justify-between items-center"
          >
            <div className="flex flex-col">
              <span className="font-medium">{backup.description || 'Certificate Backup'}</span>
              <div className="flex items-center text-xs text-muted-foreground">
                <Clock className="h-3 w-3 mr-1" />
                {formatDistance(backup.timestamp, new Date(), { addSuffix: true })}
                <span className="mx-2">•</span>
                <span>{backup.itemCount} templates</span>
              </div>
            </div>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleRestore(backup.id)}
              disabled={isRestoring !== null}
              className="h-8 px-2"
            >
              {isRestoring === backup.id ? (
                <span className="flex items-center">
                  <Clock className="h-3 w-3 mr-1 animate-spin" />
                  Restoring...
                </span>
              ) : (
                <span className="flex items-center">
                  <Download className="h-3 w-3 mr-1" />
                  Restore
                </span>
              )}
            </Button>
          </Card>
        ))}
      </div>
    </ScrollArea>
  );
};

export default CertificateBackupList;
